import jwtDecode from "jwt-decode";
import { getPropertyById, getUserDetails } from "../../../../SOA/api";
import { getTenantDetails, getMoveInOutInspectionById } from "./Api";

export const PHASE = {
  LANDLORD_INITIAL: 1,
  TENANT_ACK: 2,
  LANDLORD_FINAL: 3,
};

const defaultPhase = () => ({
  [PHASE.LANDLORD_INITIAL]: {
    inspection_date: null,
    landlord_name: "",
    landlord_signature: "",
    landlord_signature_date: null,
  },
  [PHASE.TENANT_ACK]: {
    a: { tenant_name: "", tenant_signature: "" },
    b: { tenant_name: "", disagreement_reasons: "" },
    c: { landlord_refusal_signature: "" },
  },
  [PHASE.LANDLORD_FINAL]: {
    inspection_date: null,
    landlord_name: "",
    landlord_signature: "",
    landlord_signature_date: null,
    forwarding_address: "",
  },
  completed_at: null,
});

export const defaultMoveInOutInspection = {
  property_details: {
    property_id: "",
    address: "",
    unit_number: "",
    landlord: { name: "" },
    tenants: [],
  },
  move_in: defaultPhase(),
  move_out: defaultPhase(),
  inspection_items: [],
  keys: {
    issued: {
      suite: 0,
      building: 0,
      entry: 0,
      garage: 0,
      mailbox: 0,
      fob: 0,
      parking_pass: 0, 
      discount_card: 0,  
    },
    returned: {
      suite: 0,
      building: 0,
      entry: 0,
      garage: 0,
      mailbox: 0,
      fob: 0,
      parking_pass: 0,    
      discount_card: 0,   
    },
  },
  move_in_conclusions_recommendations: "",
  move_out_conclusions_recommendations: ""
};

function getDecodedUser() {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
}

async function getCurrentUserInfo(decodedUser) {
  if (!decodedUser?.id || !decodedUser?.role) {
    throw new Error("Invalid user token: missing id or role");
  }

  const allowedRoles = ["technical staff", "vendor", "manager", "landlord", "agent", "inspector"];

  if (!allowedRoles.includes(decodedUser.role)) {
    return { id: decodedUser.id, role: decodedUser.role, name: "", signature: "" };
  }

  const details = await getUserDetails(decodedUser.id, decodedUser.role);
  return {
    id: decodedUser.id,
    role: decodedUser.role,
    name: details?.name || "",
    signature: details?.signature || "",
  };
}

function buildFullAddress(property) {
  const parts = [
    property.address?.street || property.address || "",
    property.city || "",
    property.province || "",
    property.postal_code || "",
  ];
  return parts.filter(Boolean).join(", ");
}

function createLandlordPhase(today, name, signature, isManager) {
  return {
    inspection_date: today,
    landlord_name: name,
    landlord_signature: isManager ? signature : "",
    landlord_signature_date: today,
  };
}

export const getPropertyDetailsForInspection = async (propertyId) => {
  if (!propertyId) throw new Error("Property ID is required");

  const user = getDecodedUser();
  if (!user) throw new Error("No valid access token found");

  const property = await getPropertyById(propertyId);
  if (!property) throw new Error("Property not found");

  const currentUser = await getCurrentUserInfo(user);

  return { property, currentUser };
};

// The landlord line on the sheet: "Company / Partnership label", both from the
// property record. The signing phases keep the agent's own name.
export const landlordLineFor = (property) => {
  const company = String(property?.company || property?.company_name || "").trim();
  const partnership = String(property?.partnershipLabel || property?.partnership_label || "").trim();
  return [company, partnership].filter(Boolean).join(" / ");
};

export const autoFillPropertyDetails = async (
  currentValues,
  propertyId,
  buildingName,
  unitNumber = "",
  phasePreset = null
) => {
  if (!propertyId) {
    return {
      ...currentValues,
      property_details: defaultMoveInOutInspection.property_details,
      move_in: defaultPhase(),
      move_out: defaultPhase(),
    };
  }

  const { property, currentUser } = await getPropertyDetailsForInspection(propertyId);

  const today = new Date().toISOString().split("T")[0];
  const address = buildFullAddress(property);

  const agentName = currentUser.name || property.landlord?.name || property.landlord || "";
  const agentSignature = currentUser.signature || agentName;
  const isManager = currentUser.role === "manager";

  const landlordInitial = createLandlordPhase(today, agentName, agentSignature, isManager);
  const landlordFinal = {
    ...landlordInitial,
    forwarding_address: "",
  };

  let tenants = currentValues.property_details?.tenants || [];
  if (unitNumber) {
    try {
      const resp = await getTenantDetails(propertyId, buildingName, unitNumber);
      if (resp?.success && resp.data?.tenants) {
        tenants = resp.data.tenants;
      }
    } catch (err) {
      console.warn("Could not load tenants", err);
    }
  }

  const shouldFillMoveInPhase1 = !phasePreset || phasePreset === 1;
  const shouldFillMoveInPhase3 = !phasePreset || phasePreset === 2;

  const shouldFillMoveOutPhase1 = !phasePreset || phasePreset === 3;
  const shouldFillMoveOutPhase3 = !phasePreset || phasePreset === 4;

  return {
    ...currentValues,

    property_details: {
      property_id: propertyId,
      address,
      unit_number: unitNumber || currentValues.property_details?.unit_number || "",
      landlord: { name: landlordLineFor(property) || property.landlord || agentName },
      tenants,
    },

    move_in: {
      ...currentValues.move_in,
      ...(shouldFillMoveInPhase1 && { [PHASE.LANDLORD_INITIAL]: landlordInitial }),
      ...(shouldFillMoveInPhase3 && { [PHASE.LANDLORD_FINAL]: landlordFinal }),
    },

    move_out: {
      ...currentValues.move_out,
      ...(shouldFillMoveOutPhase1 && { [PHASE.LANDLORD_INITIAL]: landlordInitial }),
      ...(shouldFillMoveOutPhase3 && { [PHASE.LANDLORD_FINAL]: landlordFinal }),
    },
  };
};

export const getMoveInOutInspectionInitialValues = async (id) => {
  if (!id) return defaultMoveInOutInspection;

  try {
    const inspection = await getMoveInOutInspectionById(id);

    const mapPhase = (data = {}) => ({
      [PHASE.LANDLORD_INITIAL]: {
        inspection_date: data?.[PHASE.LANDLORD_INITIAL]?.inspection_date || null,
        landlord_name: data?.[PHASE.LANDLORD_INITIAL]?.landlord_name || "",
        landlord_signature: data?.[PHASE.LANDLORD_INITIAL]?.landlord_signature || "",
        landlord_signature_date: data?.[PHASE.LANDLORD_INITIAL]?.landlord_signature_date || null,
      },
      [PHASE.TENANT_ACK]: {
        a: { tenant_name: data?.[PHASE.TENANT_ACK]?.a?.tenant_name || "", tenant_signature: data?.[PHASE.TENANT_ACK]?.a?.tenant_signature || "" },
        b: { tenant_name: data?.[PHASE.TENANT_ACK]?.b?.tenant_name || "", disagreement_reasons: data?.[PHASE.TENANT_ACK]?.b?.disagreement_reasons || "" },
        c: { landlord_refusal_signature: data?.[PHASE.TENANT_ACK]?.c?.landlord_refusal_signature || "" },
      },
      [PHASE.LANDLORD_FINAL]: {
        inspection_date: data?.[PHASE.LANDLORD_FINAL]?.inspection_date || null,
        landlord_name: data?.[PHASE.LANDLORD_FINAL]?.landlord_name || "",
        landlord_signature: data?.[PHASE.LANDLORD_FINAL]?.landlord_signature || "",
        landlord_signature_date: data?.[PHASE.LANDLORD_FINAL]?.landlord_signature_date || null,
        forwarding_address: data?.[PHASE.LANDLORD_FINAL]?.forwarding_address || "",
      },
      completed_at: data?.completed_at || null,
    });

    return {
      property_details: {
        property_id: inspection.property_details?.property_id?._id || inspection.property_details?.property_id || "",
        address: inspection.property_details?.address || "",
        unit_number: inspection.property_details?.unit_number || "",
        landlord: { name: inspection.property_details?.landlord?.name || "" },
        tenants: inspection.property_details?.tenants || [],
      },
      move_in: mapPhase(inspection.move_in),
      move_out: mapPhase(inspection.move_out),
      inspection_items: inspection.inspection_items || [],
      keys: {
        issued: {
          suite: inspection.keys?.issued?.suite ?? 0,
          building: inspection.keys?.issued?.building ?? 0,
          entry: inspection.keys?.issued?.entry ?? 0,
          garage: inspection.keys?.issued?.garage ?? 0,
          mailbox: inspection.keys?.issued?.mailbox ?? 0,
          fob: inspection.keys?.issued?.fob ?? 0,
          parking_pass: inspection.keys?.issued?.parking_pass ?? 0,     
          discount_card: inspection.keys?.issued?.discount_card ?? 0,  
        },
        returned: {
          suite: inspection.keys?.returned?.suite ?? 0,
          building: inspection.keys?.returned?.building ?? 0,
          entry: inspection.keys?.returned?.entry ?? 0,
          garage: inspection.keys?.returned?.garage ?? 0,
          mailbox: inspection.keys?.returned?.mailbox ?? 0,
          fob: inspection.keys?.returned?.fob ?? 0,
          parking_pass: inspection.keys?.returned?.parking_pass ?? 0,    
          discount_card: inspection.keys?.returned?.discount_card ?? 0,  
        },
      },
      move_in_conclusions_recommendations: inspection.move_in_conclusions_recommendations || "",
      move_out_conclusions_recommendations: inspection.move_out_conclusions_recommendations || ""
    };
  } catch (err) {
    console.error("Failed to load inspection", err);
    return defaultMoveInOutInspection;
  }
};